import React, { useState, useEffect } from 'react';
import { marketLinksAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Storefront, 
  ArrowSquareOut, 
  Tag, 
  Spinner,
  Package,
  Desktop,
  Wrench,
  ShoppingCart,
  Lightbulb
} from '@phosphor-icons/react';

const categoryIcons = {
  'Software': Desktop,
  'Website': ArrowSquareOut,
  'Tools': Wrench,
  'Amazon': ShoppingCart,
  'Affiliate': Tag,
  'Other': Lightbulb,
};

const categoryColors = {
  'Software': 'bg-blue-100 text-blue-800 border-blue-200',
  'Website': 'bg-purple-100 text-purple-800 border-purple-200',
  'Tools': 'bg-green-100 text-green-800 border-green-200',
  'Amazon': 'bg-orange-100 text-orange-800 border-orange-200',
  'Affiliate': 'bg-pink-100 text-pink-800 border-pink-200',
  'Other': 'bg-gray-100 text-gray-800 border-gray-200',
};

export const MarketSourcePage = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await marketLinksAPI.getAll();
      setLinks(response.data);
    } catch (error) {
      toast.error('Failed to load market links');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ['All', ...new Set(links.map(link => link.category))];

  // Filter links by category
  const filteredLinks = selectedCategory === 'All' 
    ? links 
    : links.filter(link => link.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8 animate-spin text-[#FF5A36]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 pb-24 md:pb-6">
      <div className="mb-6">
        <h1 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212]">Market Source</h1>
        <p className="text-[#6B7280] mt-1">Useful tools, software आणि affiliate links तुमच्या business साठी</p>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => {
          const Icon = categoryIcons[category] || Tag;
          return (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className={`rounded-full ${
                selectedCategory === category 
                  ? 'bg-[#FF5A36] hover:bg-[#E04828] text-white' 
                  : 'hover:border-[#FF5A36] hover:text-[#FF5A36]'
              }`}
            >
              {category !== 'All' && <Icon className="h-4 w-4 mr-1" weight="duotone" />}
              {category}
            </Button>
          );
        })}
      </div>

      {/* Links Grid */}
      {filteredLinks.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="py-16 text-center">
            <Storefront className="h-16 w-16 text-gray-300 mx-auto mb-4" weight="duotone" />
            <p className="text-gray-500">No links available yet</p>
            <p className="text-sm text-gray-400 mt-1">Check back later for useful resources</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLinks.map((link) => {
            const Icon = categoryIcons[link.category] || Tag;
            return (
              <Card 
                key={link.id} 
                className="border-[#E5E7EB] hover:border-[#FF5A36] hover:shadow-lg transition-all group overflow-hidden"
              >
                {link.image_url && (
                  <div className="h-40 overflow-hidden">
                    <img 
                      src={link.image_url} 
                      alt={link.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardContent className={link.image_url ? 'pt-4' : 'pt-6'}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#FF5A36] transition-colors">
                      {link.title}
                    </h3>
                    <Badge className={`${categoryColors[link.category] || categoryColors['Other']} text-xs flex-shrink-0`}>
                      <Icon className="h-3 w-3 mr-1" weight="fill" />
                      {link.category}
                    </Badge>
                  </div>
                  
                  {link.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {link.description}
                    </p>
                  )}
                  
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#FF5A36] hover:text-[#E04828] font-medium text-sm"
                  >
                    Visit Link
                    <ArrowSquareOut className="h-4 w-4" weight="bold" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-8 border-orange-200 bg-orange-50/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="h-6 w-6 text-orange-600" weight="fill" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Pro Tip</h3>
              <p className="text-sm text-gray-600 mt-1">
                या links वापरून तुम्ही तुमचा business grow करू शकता. Software, tools आणि affiliate programs 
                तुम्हाला additional income मिळवून देऊ शकतात!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketSourcePage;
